import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CrewMerchantUi } from 'corso-types';
import api from '~/api';
import { useToast } from '~/hooks/useToast';
import { useStoreId } from '~/providers/StoreUserProvider';
import { isErrorMessage } from '~/utils/errorResponse';

const useQueryKey = () => {
  const storeId = useStoreId();
  return ['storeMetafields', storeId] as const;
};

export const useStoreMetafields = () => {
  const queryKey = useQueryKey();

  return useQuery({
    queryKey,
    queryFn: ({ queryKey: [, storeId] }) =>
      api.store(storeId).storeMetafields.get(),
  });
};

export const usePlatformMetafields = (enabled: boolean) => {
  const storeId = useStoreId();

  return useQuery({
    queryKey: ['platformMetafields', storeId] as const,
    queryFn: ({ queryKey: [, id] }) => api.store(id).storeMetafields.platform(),
    enabled,
  });
};

export const useStoreMetafieldsCreate = () => {
  const queryClient = useQueryClient();
  const storeId = useStoreId();
  const queryKey = useQueryKey();
  const toast = useToast();

  return useMutation({
    mutationFn: (create: CrewMerchantUi.StoreMetafieldCreate) =>
      api.store(storeId).storeMetafields.create(create),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: (error) => {
      toast.show(
        isErrorMessage(error)
          ? error.message
          : 'Something went wrong. Please try again.',
      );
    },
  });
};

export const useStoreMetafieldsDelete = () => {
  const queryClient = useQueryClient();
  const storeId = useStoreId();
  const queryKey = useQueryKey();
  const toast = useToast();

  return useMutation({
    mutationFn: (storeMetafield: CrewMerchantUi.StoreMetafield) =>
      api.store(storeId).storeMetafields.delete(String(storeMetafield.id)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onMutate: (storeMetafield) => {
      const previous =
        queryClient.getQueryData<CrewMerchantUi.StoreMetafield[]>(queryKey) ??
        [];

      queryClient.setQueryData(
        queryKey,
        previous.filter((m) => m.id !== storeMetafield.id),
      );

      return { previous };
    },
    onError: (error, _, context) => {
      queryClient.setQueryData(queryKey, context?.previous);

      toast.show(
        isErrorMessage(error)
          ? error.message
          : 'Something went wrong. Please try again.',
      );
    },
  });
};
